import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { BeAGuardian } from '@/components/be-guardian-card'
import { AmortIcon } from '@/components/icons/amort'
import { CO2Icon } from '@/components/icons/co2'
import { HiveIcon } from '@/components/icons/hive'
import { PsychiatryIcon } from '@/components/icons/psychiatry'
import { Skeleton } from '@/components/skeleton'
import { Carousel } from '@/components/slider'
import { TestimonialsModal } from '@/components/testimonials/testimonials-modal'
import { useAuth } from '@/contexts'
import { Banners } from '@/models/banner'
import type { TotalNumberOfBiomes } from '@/models/biomes'
import type { MainCoordinates } from '@/models/coordinates'
import type { Property } from '@/models/property'
import { Testimonial } from '@/models/testimonial'
import api from '@/services/api'
import { normalizeText } from '@/utils/normalize-text'

import { CarbonBanner } from './banner/carbon'
import { NationalConfederationBanner } from './banner/national-confederation'
import { PreservationBanner } from './banner/preservation'
import { Banner } from './components/banner'
import { FeaturedProperties } from './components/featured-properties'
import { NewProjects } from './components/new-projects'
import { OurPreservationAreas } from './components/our-preservation-areas'
import { ShowMap } from './components/show-map'
import { TestimonialsHomeSection } from './components/testimonials'
import { ServiceCard } from './service-card'

export function Home() {
  const { signed } = useAuth()
  const [limit, setLimit] = useState<number>(8)
  const [loading, setLoading] = useState<boolean>(true)
  const [biomeAreas, setBiomeAreas] = useState<TotalNumberOfBiomes[]>([])
  const [properties, setProperties] = useState<Property[] | []>([])
  const [featuredProperties, setFeaturedProperties] = useState<Property[] | []>(
    [],
  )
  const [totalProperties, setTotalProperties] = useState<number>(0)
  const [sliderMargin, setSliderMargin] = useState<{
    left: number
    right: number
  }>({
    left: 60,
    right: 60,
  })
  const [mainCoordinates, setMainCoordinates] = useState<
    MainCoordinates[] | null
  >(null)

  const [filter, setFilter] = useState('')
  const [biome, setBiome] = useState('')
  const [mapStyle, setMapStyle] = useState('satellite-streets-v12')
  const [banners, setBanners] = useState<Banners[] | null>()
  const [testimonials, setTestimonials] = useState<Testimonial[] | null>()
  const [selectedTestimonial, setSelectedTestimonial] =
    useState<Testimonial | null>(null)
  const [isTestimonyModalOpen, setIsTestimonyModalOpen] = useState(false)

  const numberOfFeaturedHomeProperties = Number(
    import.meta.env.VITE_MAX_FEATURED_HOME_PROPERTIES ??
      window.env.MAX_FEATURED_HOME_PROPERTIES,
  )

  function handleStyleChange(style: string) {
    setMapStyle(style)
  }

  const staticBanners = [
    { component: <CarbonBanner /> },
    { component: <NationalConfederationBanner /> },
    { component: <PreservationBanner /> },
  ]

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 425) {
        return setSliderMargin({
          left: 8,
          right: 8,
        })
      }

      if (window.innerWidth < 640) {
        return setSliderMargin({
          left: 24,
          right: 24,
        })
      }

      return setSliderMargin({
        left: 60,
        right: 60,
      })
    }
    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const services = [
    {
      title: 'Preservação',
      linkUrl: '/preservation',
      Icon: <PsychiatryIcon className="size-12" />,
    },
    {
      title: 'Regeneração',
      linkUrl: '/regeneration',
      Icon: <HiveIcon className="size-12" />,
    },
    {
      title: 'Amortecimento',
      linkUrl: '/damping',
      Icon: <AmortIcon className="size-12" />,
    },
    {
      title: 'Carbono',
      linkUrl: '/carbon',
      Icon: <CO2Icon className="size-16" />,
    },
  ]

  useEffect(() => {
    async function getBiomes() {
      await api
        .get<TotalNumberOfBiomes[]>('/biomes/totalarea')
        .then(({ data }) => setBiomeAreas(data))
    }
    async function getBanners() {
      await api.get<Banners[]>('/banners').then(({ data }) => {
        setBanners(data)
      })
    }
    async function getFeaturedProperties() {
      await api
        .get(`/property/featured/${numberOfFeaturedHomeProperties}`)
        .then(({ data }) => {
          setFeaturedProperties(data)
        })
    }

    Promise.all([getBanners(), getFeaturedProperties(), getBiomes()]).catch(
      (error) => {
        toast.error(error.response.data.message)
      },
    )
  }, [numberOfFeaturedHomeProperties])

  useEffect(() => {
    async function getTestimonials() {
      await api
        .get<Testimonial[]>(`/depoimentos?limit=3&order=random`)
        .then(({ data }) => setTestimonials(data))
        .catch((error) => {
          toast.error(error.response.data.message)
        })
    }
    getTestimonials()
  }, [])

  const getLastProperties = useCallback(() => {
    async function fetchProperties() {
      await api
        .get(
          `/last/property/${limit}?filtro=${normalizeText(filter)}&bioma=${biome}`,
        )
        .then((response) => {
          setProperties(response.data.properties)
          setTotalProperties(response.data.total)
        })
        .catch((error) => toast.error(error.response.data.message))
        .finally(() => setLoading(false))
    }
    fetchProperties()
  }, [biome, filter, limit])

  useEffect(() => {
    const timeout1 = setTimeout(() => {
      getLastProperties()
    }, 1000)

    return () => {
      clearTimeout(timeout1)
    }
  }, [getLastProperties])

  const getPropertiesCoordinates = useCallback(() => {
    async function fetchPropertiesCoordinates() {
      await api
        .get<MainCoordinates[]>(
          `/property/coordinates/main?filtro=${normalizeText(filter)}&bioma=${biome}`,
        )
        .then((response) => setMainCoordinates(response.data))
        .catch((error) => toast.error(error.response.data.message))
    }
    fetchPropertiesCoordinates()
  }, [filter, biome])

  useEffect(() => {
    const timeout2 = setTimeout(() => {
      getPropertiesCoordinates()
    }, 1000)

    return () => {
      clearTimeout(timeout2)
    }
  }, [getPropertiesCoordinates])

  function loadMoreProjects() {
    if (properties.length === totalProperties || loading) return
    setLimit((prevLimit) => prevLimit + 8)
  }

  const handleOpenModal = useCallback((testimonial: Testimonial) => {
    setSelectedTestimonial(testimonial)
    setIsTestimonyModalOpen(true)
  }, [])

  return (
    <div className="flex w-full flex-col">
      {/* Seção - Slide da home */}
      <div className="h-full w-full bg-transparent">
        {!banners ? (
          <Skeleton className="h-banner w-full rounded-none object-cover" />
        ) : (
          <Carousel
            itemsPerPage={1}
            autoplayDelay={15000}
            marginRight={sliderMargin.right}
            marginLeft={sliderMargin.left}
            spaceBetween={20}
          >
            {banners.length <= 0
              ? staticBanners.map((banner, index) => {
                  return (
                    <div key={index} className="h-banner w-full">
                      {banner.component}
                    </div>
                  )
                })
              : banners.map((banner) => {
                  return (
                    <div key={banner.banner_id} className="h-banner w-full">
                      <Banner banner={banner} />
                    </div>
                  )
                })}
          </Carousel>
        )}
      </div>

      <div className="bg-monochromatic-200 flex flex-col gap-10 px-5 py-10 xl:px-12 2xl:gap-20 2xl:px-24 2xl:py-20">
        {/* Seção - Conheça nossas áreas de conservação */}
        <OurPreservationAreas
          biome={biome}
          biomeAreas={biomeAreas}
          filter={filter}
          setBiome={setBiome}
          setFilter={setFilter}
        />
        {/* Seção - Novos projetos */}
        <NewProjects
          biome={biome}
          filter={filter}
          loadMoreProjects={loadMoreProjects}
          loading={loading}
          properties={properties}
          propertiesTotal={totalProperties}
        />
        {/* Seção - Conheça nossas áreas de conservação */}
        <OurPreservationAreas
          biome={biome}
          biomeAreas={biomeAreas}
          filter={filter}
          setBiome={setBiome}
          setFilter={setFilter}
        />
        <ShowMap
          handleStyleChange={handleStyleChange}
          mainCoordinates={mainCoordinates}
          mapStyle={mapStyle}
        />
        {featuredProperties.length <= 0 && (
          <Skeleton className="h-96 w-full rounded-2xl object-cover lg:min-h-125 2xl:min-h-150" />
        )}
        {featuredProperties && (
          <div className="w-full">
            <Carousel itemsPerPage={1} spaceBetween={20}>
              {featuredProperties.map((prop) => {
                return (
                  <FeaturedProperties
                    key={prop.id_propriedade}
                    property={prop}
                  />
                )
              })}
            </Carousel>
          </div>
        )}
        {/* Seção - Saiba mais sobre nossos serviços ambientais */}
        <div className="space-y-6">
          <h1 className="title w-full">
            Saiba mais sobre nossos serviços ambientais
          </h1>
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {services.map(({ title, linkUrl, Icon }) => (
              <ServiceCard
                key={title}
                title={title}
                linkUrl={linkUrl}
                Icon={Icon}
              />
            ))}
          </div>
        </div>
        {/* Seção - Depoimentos */}
        {testimonials && testimonials.length > 0 && (
          <TestimonialsHomeSection
            testimonials={testimonials}
            handleOpenModal={handleOpenModal}
          />
        )}
        <TestimonialsModal
          personName={selectedTestimonial?.nome_pessoa || ''}
          profilePhoto={selectedTestimonial?.foto_pessoa || ''}
          testimonial={selectedTestimonial?.depoimento || ''}
          description={selectedTestimonial?.descricao_pessoa || ''}
          isModalOpen={isTestimonyModalOpen}
          setIsModalOpen={setIsTestimonyModalOpen}
        />
        {/* <Dialog
          open={isTestimonyModalOpen}
          onOpenChange={setIsTestimonyModalOpen}
        >
          <DialogContent className="h-auto max-w-3xl gap-6">
            <DialogHeader className="flex flex-row items-center gap-4">
              <Avatar className="size-16 select-none">
                <AvatarImage
                  src={selectedTestimonial?.foto_pessoa}
                  alt="testimonial's avatar"
                  className="size-full object-cover"
                />
                <AvatarFallback
                  delayMs={300}
                  className="bg-bios-300/50 size-16 text-lg font-bold select-none"
                >
                  {selectedTestimonial?.nome_pessoa[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="text-bios-title flex flex-col text-start">
                <DialogTitle className="text-bios-title text-left">
                  {selectedTestimonial?.nome_pessoa}
                </DialogTitle>
                <p className="text-bios-title">
                  {selectedTestimonial?.descricao_pessoa}
                </p>
              </div>
            </DialogHeader>
            <ScrollArea className="pr-4">
              <div
                className="max-h-[60vh]"
                dangerouslySetInnerHTML={createMarkup(
                  selectedTestimonial?.depoimento || '',
                )}
              />
            </ScrollArea>
          </DialogContent>
        </Dialog> */}
        {/* Seção - Seja um guardião */}
        {!signed && <BeAGuardian className="mb-16" />}
      </div>
    </div>
  )
}
